import React, { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { Card, Button, Table, Tag, Flex, Typography, Dropdown } from "antd"
import { MoreOutlined, DeleteOutlined, CheckOutlined, LeftOutlined } from "@ant-design/icons"
import { routes } from "router/routes"
import {
  getEquipmentDetails,
  getEquipmentCalendar,
  selectEquipmentDetails,
  selectEquipmentDetailsLoading,
  selectEquipmentCalendar,
  selectEquipmentCalendarLoading,
  deactivateEquipment,
  activateEquipment,
  deleteEquipment,
  getEquipmentDeactivationDetails,
  getEquipmentDeletionDetails,
  selectDeactivateEquipmentLoading,
  selectDeleteEquipmentLoading,
  selectEquipmentDeactivationDetails,
  selectEquipmentDeactivationDetailsLoading,
  selectEquipmentDeletionDetails,
  selectEquipmentDeletionDetailsLoading,
  fetchBoatById,
  cleanBoat,
  cleanEquipmentDetails,
  updateBoat
} from "store/boats"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import EquipmentCalendar from "./EquipmentCalendar"
import DSTab from "ui/DSTab"
import styles from "./index.module.css"
import ExternalLink from "ui/Icon/DSIcon/ExternalLink"
import { MediaPreview } from "ui/MediaPreview"
import { VideoPlayerModal } from "ui/VideoPlayerModal"
import { isMediaFileVideo } from "utils/media-upload"
import { useEquipmentModals } from "../hooks/useEquipmentModals"
import { determineDeactivateMode } from "../utils/determineDeactivateMode"
import { toaster } from "components/common/Toast"
import { useTranslation } from "react-i18next"
import AddEquipmentModal from "pagesV2/pro/Equipment/AddEquipmentModal"
import NoExperiencesDeleteDeactivateModal from "../modal/NoexperiencesDeleteDeactivate"
import DeleteDeactivateModal from "../modal/DeactivateDelete"
import AssignExperiencesModal from "../modal/AssignExperiences"
import { spacings } from "themes/variables"
import { MAX_CHARACTERISTICS_DISPLAY, MAX_DESCRIPTION_LENGTH, EQUIPMENT_DELETE_DEACTIVATE_MODES } from "../constants"
import { showModal } from "modules/modals/reducer"
import { confirmModalSelector } from "modules/modals/selectors"
import IconWithBg from "ui/IconWithBg"

const { Title, Text, Paragraph } = Typography

const EquipmentDetails = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState("details")
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false)
  const [modalBoatId, setModalBoatId] = useState(null)
  const [isDuplicateMode, setIsDuplicateMode] = useState(false)
  const [videoPlayerOpen, setVideoPlayerOpen] = useState(false)
  const [selectedMedia, setSelectedMedia] = useState(null)
  const [experienceToRemove, setExperienceToRemove] = useState(null)

  // Confirmation modal state
  const confirmModal = useSelector(confirmModalSelector)
  // Select equipment details and calendar from Redux store
  const equipmentDetails = useSelector(selectEquipmentDetails)
  const detailsLoading = useSelector(selectEquipmentDetailsLoading)
  const calendarData = useSelector(selectEquipmentCalendar)
  const calendarLoading = useSelector(selectEquipmentCalendarLoading)

  // Select boat data for modal (fetched separately)
  const boat = useSelector((state) => state.boats.boat)
  const boatLoading = boat.loading

  // Redux selectors for deactivate/delete operations
  const deactivateLoading = useSelector(selectDeactivateEquipmentLoading)
  const deleteLoading = useSelector(selectDeleteEquipmentLoading)
  const equipmentDeactivationDetails = useSelector(selectEquipmentDeactivationDetails)
  const deactivationDetailsLoading = useSelector(selectEquipmentDeactivationDetailsLoading)
  const equipmentDeletionDetails = useSelector(selectEquipmentDeletionDetails)
  const deletionDetailsLoading = useSelector(selectEquipmentDeletionDetailsLoading)

  // Custom hook for modal state management (only for deactivate/delete modals)
  const {
    deactivateDeleteModalState,
    openDeactivateModal,
    openDeleteModal,
    closeDeactivateDeleteModal,
    setDeactivateDeleteDetails
  } = useEquipmentModals()

  // Equipment modal state (managed locally like in the table)
  const [showEquipmentModal, setShowEquipmentModal] = useState(false)

  // Assign Experiences modal state
  const [showAssignExperiencesModal, setShowAssignExperiencesModal] = useState(false)

  // Clear old equipment details when ID changes to prevent showing stale data
  useEffect(() => {
    // Clear previous equipment details to show loading instead of old data
    dispatch(cleanEquipmentDetails())

    // Cleanup on unmount
    return () => {
      dispatch(cleanEquipmentDetails())
    }
  }, [id, dispatch])

  // Fetch equipment details on mount
  useEffect(() => {
    if (id) {
      dispatch(getEquipmentDetails(id))
    }
  }, [dispatch, id])

  // Fetch calendar data when user switches to calendar tab
  useEffect(() => {
    if (id && activeTab === "calendar") {
      dispatch(getEquipmentCalendar(id))
    }
  }, [dispatch, id, activeTab])

  // Fetch boat data for modal when modal opens
  useEffect(() => {
    if (modalBoatId && showEquipmentModal) {
      dispatch(fetchBoatById(modalBoatId))
    }
  }, [modalBoatId, showEquipmentModal, dispatch])

  /**
   * Fetch equipment deactivation details when modal is opened
   * Sets the details in modal state once loaded
   */
  useEffect(() => {
    if (equipmentDeactivationDetails && deactivateDeleteModalState.visible && deactivateDeleteModalState.action === "deactivate") {
      // Determine mode from API response or compute it
      const mode = equipmentDeactivationDetails.mode || determineDeactivateMode(equipmentDeactivationDetails)

      setDeactivateDeleteDetails({
        ...equipmentDeactivationDetails,
        mode
      })
    }
  }, [equipmentDeactivationDetails, deactivateDeleteModalState.visible, deactivateDeleteModalState.action, setDeactivateDeleteDetails])

  /**
   * Fetch equipment deletion details when modal is opened
   * Sets the details in modal state once loaded
   */
  useEffect(() => {
    if (equipmentDeletionDetails && deactivateDeleteModalState.visible && deactivateDeleteModalState.action === "delete") {
      // Determine mode from API response or compute it
      const mode = equipmentDeletionDetails.mode || determineDeactivateMode(equipmentDeletionDetails)

      setDeactivateDeleteDetails({
        ...equipmentDeletionDetails,
        mode
      })
    }
  }, [equipmentDeletionDetails, deactivateDeleteModalState.visible, deactivateDeleteModalState.action, setDeactivateDeleteDetails])

  // Extract the actual equipment data from the API response wrapper
  const equipmentData = equipmentDetails?.data

  // Map API response to component structure
  const equipment = equipmentData ? {
    id: equipmentData.id,
    name: equipmentData.header?.name || equipmentData.equipment_title,
    description: equipmentData.details?.description || equipmentData.description,
    is_active: equipmentData.status === "active",
    max_users: equipmentData.max_guests,
    category: {
      id: equipmentData.category?.id || equipmentData.category_id,
      name: equipmentData.header?.category || equipmentData.category?.name || equipmentData.boat_category?.name
    },
    characteristics: equipmentData.details?.characteristics || [],
    // Combine all media types (photos, videos, cropped_photos, trimmed_videos) into a single array
    media: [
      ...(equipmentData.media_gallery?.photos || []),
      ...(equipmentData.media_gallery?.videos || []),
      ...(equipmentData.media_gallery?.cropped_photos || []),
      ...(equipmentData.media_gallery?.trimmed_videos || [])
    ].sort((a, b) => (a.position || 0) - (b.position || 0)), // Sort by position if available
    trips: equipmentData.assigned_experiences || []
  } : null

  // Map API upcoming bookings to component structure
  const mapUpcomingBookings = (apiBookings) => {
    if (!apiBookings || apiBookings.length === 0) return []

    // Group bookings by date
    const groupedByDate = apiBookings.reduce((acc, booking) => {
      const date = new Date(booking.booked_date).toLocaleDateString('en-US', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })

      if (!acc[date]) {
        acc[date] = []
      }

      acc[date].push({
        id: booking.id,
        experienceName: booking.trip_title,
        time: booking.booked_time,
        bookingId: booking.booking_number,
        clientName: booking.client_name,
        crewMembers: booking.passenger_count || 0,
        equipment: booking.assets_used?.length || 0,
        paymentStatus: booking.payment_status === "outstanding" ? "Payment Outstanding" : "Paid"
      })

      return acc
    }, {})

    // Convert to array format expected by component
    return Object.entries(groupedByDate).map(([date, bookings]) => ({
      date,
      bookings
    }))
  }

  // Use API bookings if available, otherwise use empty array
  const upcomingBookings = equipmentData?.upcoming_bookings
    ? mapUpcomingBookings(equipmentData.upcoming_bookings)
    : []

  // Table columns for bookings
  const bookingColumns = [
    {
      title: t("equipment.details.columns.experience_name"),
      dataIndex: "experienceName",
      key: "experienceName",
      render: (text) => <a href="#" target="_blank" rel="noopener noreferrer" className={styles.experienceLink}>{text} <ExternalLink /></a>
    },
    {
      title: t("equipment.details.columns.time"),
      dataIndex: "time",
      key: "time"
    },
    {
      title: t("equipment.details.columns.id"),
      dataIndex: "bookingId",
      key: "bookingId"
    },
    {
      title: t("equipment.details.columns.client_name"),
      dataIndex: "clientName",
      key: "clientName"
    },
    {
      title: t("equipment.details.columns.crew_members"),
      dataIndex: "crewMembers",
      key: "crewMembers"
    },
    {
      title: t("equipment.details.columns.equipment"),
      dataIndex: "equipment",
      key: "equipment"
    },
    {
      title: t("equipment.details.columns.payment_status"),
      dataIndex: "paymentStatus",
      key: "paymentStatus",
      render: (status) => (
        <Tag color={status === "Paid" ? "green" : "orange"}>{status}</Tag>
      )
    }
  ]

  // Use metadata count if available, otherwise calculate from array
  const totalBookingsCount = equipmentDetails?.metadata?.upcoming_bookings_count
    || upcomingBookings.reduce((total, dateGroup) => total + dateGroup.bookings.length, 0)

  // Details card truncation logic
  const description = equipment?.description || ""
  const characteristics = equipment?.characteristics || []

  const isDescriptionLong = description.length > MAX_DESCRIPTION_LENGTH
  const hasManyCharacteristics = characteristics.length > MAX_CHARACTERISTICS_DISPLAY

  const shouldShowSeeMore = isDescriptionLong || hasManyCharacteristics

  const displayDescription = isDetailsExpanded || !isDescriptionLong
    ? description
    : description.substring(0, MAX_DESCRIPTION_LENGTH) + "..."

  const displayCharacteristics = isDetailsExpanded || !hasManyCharacteristics
    ? characteristics
    : characteristics.slice(0, MAX_CHARACTERISTICS_DISPLAY)

  const handleToggleDetails = () => {
    setIsDetailsExpanded(!isDetailsExpanded)
  }

  /**
   * Show confirmation modal before removing an experience
   */
  const handleRemoveExperience = (experienceIdToRemove) => {
    // Find the experience name for the confirmation message
    const experienceToDelete = equipment?.trips?.find(trip => trip.id === experienceIdToRemove)
    const experienceName = experienceToDelete?.title || "this experience"

    // Store the ID to remove and show confirmation modal
    setExperienceToRemove(experienceIdToRemove)
    dispatch(showModal("confirmModal", {
      title: t("equipment.confirm_remove_experience", {
        defaultValue: `Are you sure you want to remove <b>${experienceName}</b> from this equipment?`
      }),
      submitText: t("common.remove", { defaultValue: "Remove" }),
      cancelText: t("common.cancel", { defaultValue: "Cancel" }),
      color: "danger"
    }))
  }

  /**
   * Actually remove the experience after confirmation
   */
  const executeRemoveExperience = (experienceIdToRemove) => {
    // Get all current trip IDs except the one being removed
    const remainingTripIds = equipment?.trips
      ?.filter(trip => trip.id !== experienceIdToRemove)
      ?.map(trip => trip.id) || []

    // Build FormData with only trip_ids
    const formData = new FormData()
    for (const tripId of remainingTripIds) {
      formData.append("boat[trip_ids][]", tripId)
    }

    // If no experiences remain, send empty array indicator
    if (remainingTripIds.length === 0) {
      formData.append("boat[trip_ids][]", "")
    }

    dispatch(updateBoat(id, formData))
      .then(() => {
        toaster.success(t("equipment.experience_removed_successfully", { defaultValue: "Experience removed successfully" }))
        // Refresh equipment details to update the list
        dispatch(getEquipmentDetails(id))
      })
      .catch(() => {
        toaster.error(t("equipment.experience_remove_failed", { defaultValue: "Failed to remove experience" }))
      })
  }

  // Handle confirmation modal response for experience removal
  useEffect(() => {
    if (confirmModal?.submit === true && experienceToRemove) {
      executeRemoveExperience(experienceToRemove)
      setExperienceToRemove(null)
    } else if (confirmModal?.submit === false && experienceToRemove) {
      setExperienceToRemove(null)
    }
  }, [confirmModal?.submit])

  // Table columns for assigned experiences
  const experienceColumns = [
    {
      title: t("equipment.details.columns.experience_name"),
      dataIndex: "title",
      key: "title",
      render: (_, record) => record.title
    },
    {
      title: t("equipment.details.columns.guests"),
      dataIndex: "max_guests",
      key: "max_guests",
      align: "center",
      render: (_, record) => record.max_guests
    },
    {
      title: t("equipment.details.columns.price"),
      key: "price",
      align: "right",
      render: (_, record) => {
        const minPrice = Number.parseFloat(record.min_price || 0)
        const maxPrice = Number.parseFloat(record.max_price || 0)
        const currency = record.currency || "$"

        if (minPrice === maxPrice) {
          return `${currency}${minPrice.toFixed(2)}`
        }
        return `${currency}${minPrice.toFixed(2)} - ${currency}${maxPrice.toFixed(2)}`
      }
    },
    {
      title: t("equipment.details.columns.status"),
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const isPublished = status.toLowerCase() === "published"
        return (
          <Tag
            color={isPublished ? "green" : "default"}
            icon={isPublished ? <CheckOutlined /> : null}
            className={styles.statusTag}
          >
            {status}
          </Tag>
        )
      }
    },
    {
      title: "",
      key: "action",
      align: "right",
      width: 50,
      render: (_, record) => (
        <Button
          type="text"
          icon={<DeleteOutlined />}
          className={styles.deleteButton}
          onClick={() => handleRemoveExperience(record.id)}
        />
      )
    }
  ]

  const handleBack = () => {
    navigate(routes.guideResourcesPath())
  }

  /**
   * Handle media click in gallery
   * Opens video player modal if media is a video
   */
  const handleMediaClick = (mediaItem) => {
    if (isMediaFileVideo(mediaItem)) {
      setSelectedMedia(mediaItem)
      setVideoPlayerOpen(true)
    }
  }

  /**
   * Handle closing video player modal
   */
  const handleCloseVideoPlayer = () => {
    setVideoPlayerOpen(false)
    setSelectedMedia(null)
  }

  /**
   * Handle edit button click
   * Opens the edit equipment modal
   */
  const handleEdit = () => {
    if (!id) {
      toaster.error(t("equipment.errors.no_equipment_selected"))
      return
    }
    // Set boat ID and open modal - boat data will be fetched via useEffect
    setIsDuplicateMode(false)
    setShowEquipmentModal(true)
    setModalBoatId(Number(id))
  }

  /**
   * Handle duplicate button click
   * Opens add equipment modal in duplicate mode
   */
  const handleDuplicate = () => {
    if (!id) {
      toaster.error(t("equipment.errors.no_equipment_selected"))
      return
    }
    // Set duplicate mode, boat ID and open modal - boat data will be fetched via useEffect
    setIsDuplicateMode(true)
    setShowEquipmentModal(true)
    setModalBoatId(Number(id))
  }

  /**
   * Handle modal close
   * Cleans up boat data and resets modal state
   * Always refreshes equipment details since the modal might have saved changes
   */
  const handleCancelEquipmentModal = () => {
    setShowEquipmentModal(false)
    // Clear boat data to prevent showing stale data when reopening
    dispatch(cleanBoat())
    // Refresh equipment details (safe to always refresh since modal calls onCancel after save)
    dispatch(getEquipmentDetails(id))
  }

  /**
   * Handle deactivate button click
   * Fetches equipment details and opens deactivate modal
   */
  const handleDeactivate = () => {
    if (!id || !equipment) {
      toaster.error(t("equipment.errors.no_equipment_selected"))
      return
    }

    // Create equipment object compatible with modal
    const equipmentForModal = {
      key: id,
      id: id,
      title: equipment.name
    }

    // Open modal immediately (will show loading state)
    openDeactivateModal(equipmentForModal)

    // Fetch deactivation details from API
    dispatch(getEquipmentDeactivationDetails(id)).catch((error) => {
      console.error("Failed to fetch equipment details:", error)
      closeDeactivateDeleteModal()
    })
  }

  /**
   * Handle activate button click
   * Activates the equipment directly without modal
   */
  const handleActivate = () => {
    if (!id || !equipment) {
      toaster.error(t("equipment.errors.no_equipment_selected"))
      return
    }

    dispatch(activateEquipment(id))
      .then((response) => {
        // Use message from backend response if available
        const successMessage = response?.message || `${equipment.name} has been activated successfully`
        toaster.success(successMessage)
        // Refresh equipment details
        dispatch(getEquipmentDetails(id))
      })
      .catch((error) => {
        console.error("Equipment activation failed:", error)
      })
  }

  /**
   * Handle delete button click
   * Fetches equipment details and opens delete modal
   */
  const handleDelete = () => {
    if (!id || !equipment) {
      toaster.error(t("equipment.errors.no_equipment_selected"))
      return
    }

    // Create equipment object compatible with modal
    const equipmentForModal = {
      key: id,
      id: id,
      title: equipment.name
    }

    // Open modal immediately (will show loading state)
    openDeleteModal(equipmentForModal)

    // Fetch deletion details from API
    dispatch(getEquipmentDeletionDetails(id)).catch((error) => {
      console.error("Failed to fetch equipment deletion details:", error)
      closeDeactivateDeleteModal()
    })
  }

  /**
   * Handle deactivate/delete modal confirmation
   * Dispatches appropriate Redux action based on action type and mode
   * @param {object} data - Additional data for the request (mode, replacement_ids, etc.)
   */
  const handleDeactivateDeleteOk = async (data = {}) => {
    const { equipment: modalEquipment, action } = deactivateDeleteModalState

    // Equipment data uses 'key' field for ID (mapped from item.id in useEquipmentData)
    const equipmentId = modalEquipment?.key || modalEquipment?.id

    if (!equipmentId) {
      toaster.error(t("equipment.errors.no_equipment_selected"))
      return
    }

    const actionFn = action === "deactivate" ? deactivateEquipment : deleteEquipment

    dispatch(actionFn(equipmentId, data))
      .then(() => {
        toaster.success(
          action === "deactivate"
            ? t("equipment.success.deactivated", { name: modalEquipment.title })
            : t("equipment.success.deleted", { name: modalEquipment.title })
        )
        closeDeactivateDeleteModal()

        // After successful delete, navigate back to equipment list
        if (action === "delete") {
          navigate(routes.guideResourcesPath())
        } else {
          // After deactivation, refresh equipment details
          dispatch(getEquipmentDetails(id))
        }
      })
      .catch((error) => {
        console.error("Equipment operation failed:", error)
      })
  }

  /**
   * Handle "Add New Booking" button click
   * Navigate to booking creation route with filtered experiences
   */
  const handleAddNewBooking = () => {
    // Navigate to booking creation route, passing equipment's assigned experience IDs
    // The filterExperienceIds will be available in location.state
    navigate(routes.guideBookingNewPath(), {
      state: {
        filterExperienceIds: equipment?.trips?.map(trip => trip.id) || [],
        equipmentId: equipment?.id,
        equipmentName: equipment?.name
      }
    })
  }

  /**
   * Handle "Assign Experiences" button click
   * Opens the assign experiences modal
   */
  const handleAssignExperiences = () => {
    setShowAssignExperiencesModal(true)
  }

  /**
   * Handle assign experiences modal close
   */
  const handleAssignExperiencesModalClose = () => {
    setShowAssignExperiencesModal(false)
  }

  /**
   * Handle dropdown menu item clicks
   */
  const handleMenuClick = ({ key }) => {
    switch (key) {
      case "duplicate":
        handleDuplicate()
        break
      case "deactivate":
        if (equipment?.is_active) {
          handleDeactivate()
        } else {
          handleActivate()
        }
        break
      case "delete":
        handleDelete()
        break
      default:
        break
    }
  }

  const moreMenuItems = [
    {
      key: "duplicate",
      label: t("global.duplicate")
    },
    {
      key: "deactivate",
      label: equipment?.is_active ? t("global.deactivate") : t("global.activate")
    },
    {
      key: "delete",
      label: t("global.delete"),
      danger: true
    }
  ]

  const tabItems = [
    {
      key: "details",
      label: t("equipment.details.details_title")
    },
    {
      key: "calendar",
      label: "Calendar"
    }
  ]

  // Show loading state while fetching data
  if (detailsLoading && !equipmentDetails) {
    return <FirstLoading loading={detailsLoading} />
  }

  return (
    <div className={styles.container}>
      {/* Navigation Header */}
      <Flex gap={16} className={styles.navHeader}>
        <IconWithBg icon={<LeftOutlined />} onClick={handleBack} />
        <Title level={3}>Resource details</Title>
      </Flex>

      {/* Header Section */}
      <Card className={styles.headerCard}>
        <Flex justify="space-between" align="flex-start">
          <Flex gap={16} align="flex-start">
            {/* Equipment Image */}
            <div className={styles.equipmentImage}>
              {equipmentData?.header?.image || equipment?.media?.[0] ? (
                <img
                  src={equipmentData?.header?.image || equipment.media[0].url}
                  alt={equipment.name}
                  style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8 }}
                />
              ) : (
                <div className={styles.imagePlaceholder}>
                  <Text type="secondary">No Image</Text>
                </div>
              )}
            </div>

            {/* Equipment Info */}
            <Flex vertical gap={8}>
              <Flex align="center" gap={12}>
                <Title level={3} className={styles.equipmentTitle}>
                  {equipment?.name || "Equipment Name"}
                </Title>
                <Tag color={equipment?.is_active ? "green" : "red"} className={styles.statusTag}>
                  {equipment?.is_active ? "Active" : "Deactivated"}
                </Tag>
              </Flex>
              <Tag className={styles.quantityTag}>
                QTY: {equipmentData?.header?.total_assets || 0}
              </Tag>
              <Tag className={styles.categoryTag}>
                {equipment?.category?.name || "Category"}
              </Tag>
            </Flex>
          </Flex>

          {/* Action Buttons */}
          <Flex gap={8}>
            <Button type="primary" onClick={handleEdit}>
              Edit
            </Button>
            <Dropdown menu={{ items: moreMenuItems, onClick: handleMenuClick }} trigger={["click"]}>
              <Button icon={<MoreOutlined />} />
            </Dropdown>
          </Flex>
        </Flex>
      </Card>

      {/* Tabs */}
      <DSTab
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        className={styles.tabs}
      />

      {/* Content based on active tab */}
      {activeTab === "details" && (
        <>
          {/* Upcoming Bookings Section */}
          <Card className={styles.bookingsCard}>
            <Flex justify="space-between" align="center" className={styles.sectionHeader}>
              <Title level={4}>{t("equipment.details.upcoming_bookings")} <span className={styles.totalCount}>({totalBookingsCount})</span></Title>
              <Button type="primary" onClick={handleAddNewBooking}>{t("equipment.details.add_new_booking")}</Button>
            </Flex>

            <div className={styles.scrollContainer}>
              {upcomingBookings.length > 0 ? (
                upcomingBookings.map((dateGroup) => (
                  <div key={dateGroup.date} className={styles.bookingDateGroup}>
                    <div className={styles.dateHeader}>
                      <Text strong>{dateGroup.date}</Text>
                    </div>
                    <Table
                      dataSource={dateGroup.bookings}
                      columns={bookingColumns}
                      pagination={false}
                      rowKey="id"
                      className={styles.bookingsTable}
                    />
                  </div>
                ))
              ) : (
                <Flex vertical align="center" justify="center" className={styles.emptyState}>
                  <Text type="secondary">{t("equipment.details.no_upcoming_bookings")}</Text>
                </Flex>
              )}
            </div>
          </Card>

          {/* Details and Assigned Experiences Section */}
          <Flex gap={16} className={styles.detailsSection}>
            {/* Details Card */}
            <Card className={styles.detailsCard}>
              <Title level={4}>{t("equipment.details.details_title")}</Title>
              <div className={styles.scrollContainer}>
                <Paragraph className={styles.description}>
                  {displayDescription || t("equipment.details.no_description")}
                </Paragraph>

                {/* Characteristics */}
                {displayCharacteristics.length > 0 && (
                  <Flex gap={spacings.XXS} wrap="wrap" className={styles.characteristics}>
                    {displayCharacteristics.map((char) => (
                      <Tag key={char.id} className={styles.characteristicTag}>
                        {char.name}
                      </Tag>
                    ))}
                  </Flex>
                )}

                {shouldShowSeeMore && (
                  <Button type="link" className={styles.seeMore} onClick={handleToggleDetails}>
                    {isDetailsExpanded ? t("equipment.details.see_less") : t("equipment.details.see_more")}
                  </Button>
                )}
              </div>
            </Card>

            {/* Assigned Experiences Card */}
            <Card className={styles.experiencesCard}>
              <Flex justify="space-between" align="center" className={styles.experienceHeader}>
                <Title level={4}>
                  {t("equipment.details.assigned_experiences")} <span className={styles.totalCount}> ({equipmentDetails?.metadata?.assigned_experiences_count || equipment?.trips?.length || 0})</span>
                </Title>
                <Button type="link" className={styles.assignLink} onClick={handleAssignExperiences}>{t("equipment.details.assign")}</Button>
              </Flex>

              {equipment?.trips?.length > 0 ? (
                <div className={styles.scrollContainer}>
                  <Table
                    dataSource={equipment.trips}
                    columns={experienceColumns}
                    pagination={false}
                    rowKey="id"
                    className={styles.experiencesTable}
                  />
                </div>
              ) : (
                <Flex vertical align="center" justify="center" className={styles.emptyState}>
                  <div className={styles.emptyStateImage}>
                    {/* Placeholder for illustration */}
                    <Text type="secondary">{t("equipment.details.no_illustration")}</Text>
                  </div>
                  <Text type="secondary">{t("equipment.details.not_adding_to_listings")}</Text>
                  <Button type="primary" className={styles.assignButton} onClick={handleAssignExperiences}>
                    {t("equipment.details.assign")}
                  </Button>
                </Flex>
              )}
            </Card>
          </Flex>

          {/* Gallery Section */}
          <Card className={styles.galleryCard}>
            <Title level={4}>{t("equipment.details.gallery")}</Title>
            <div className={styles.gallery}>
              {equipment?.media?.length > 0 ? (
                equipment.media.map((mediaItem) => (
                  <div key={mediaItem.id} className={styles.galleryItem}>
                    <MediaPreview
                      media={mediaItem}
                      className={styles.galleryImage}
                      lazyLoad={false}
                      onClick={() => handleMediaClick(mediaItem)}
                    />
                  </div>
                ))
              ) : (
                <Text type="secondary">{t("equipment.details.no_media")}</Text>
              )}
            </div>
          </Card>
        </>
      )}

      {activeTab === "calendar" && (
        <EquipmentCalendar calendarData={calendarData} loading={calendarLoading} />
      )}

      {/* Equipment Modal - rendered directly like in the table */}
      {showEquipmentModal && (
        <AddEquipmentModal
          visible={showEquipmentModal}
          onCancel={handleCancelEquipmentModal}
          record={boat}
          boatId={modalBoatId}
          boatLoading={boatLoading}
          isDuplicateMode={isDuplicateMode}
        />
      )}

      {/* Deactivate/Delete Modals */}
      {(() => {
        const equipmentTitle = deactivateDeleteModalState?.equipment?.title || ""
        const mode = deactivateDeleteModalState?.mode
        const details = deactivateDeleteModalState?.details || {}
        const action = deactivateDeleteModalState?.action
        const expMessage = details.exp_message || null
        const bookingMessage = details.booking_message || null

        const getWarningMessage = () => {
          if (mode === EQUIPMENT_DELETE_DEACTIVATE_MODES.PARTIALLY_SUBSTITUTABLE) {
            return null
          }
          if (expMessage) return expMessage
          if (bookingMessage) return bookingMessage
          if (action === "deactivate") {
            return t("equipment.modals.warning_deactivate_remove", { name: equipmentTitle })
          }
          return t("equipment.modals.warning_delete_remove", { name: equipmentTitle })
        }

        const equipmentName = details.boat_name || equipmentTitle
        const assignedTripsCount = details.assigned_trips?.length || 0
        const activeBookingsCount = details.active_bookings?.length || 0

        let subtitle = null
        if (action === "deactivate") {
          switch (mode) {
            case EQUIPMENT_DELETE_DEACTIVATE_MODES.ASSIGNED_EXPERIENCES_NO_ACTIVE_BOOKING:
              subtitle = (
                <>
                  <strong>{equipmentName}</strong> {t("equipment.modals.is_assigned_to")} {assignedTripsCount} {t("equipment.modals.experiences")}:
                </>
              )
              break
            case EQUIPMENT_DELETE_DEACTIVATE_MODES.FULLY_SUBSTITUTABLE:
            case EQUIPMENT_DELETE_DEACTIVATE_MODES.PARTIALLY_SUBSTITUTABLE:
              subtitle = (
                <>
                  <strong>{equipmentName}</strong> {t("equipment.modals.is_assigned_to")} ({activeBookingsCount}) {t("equipment.modals.upcoming_trips")}. {t("equipment.modals.to_deactivate")} <strong>{equipmentName}</strong> {t("equipment.modals.you_must_replace_it")}.
                </>
              )
              break
            default:
              subtitle = (
                <>
                  {t("equipment.modals.deactivate_equipment_description_prefix")}
                  <strong>{equipmentName}</strong>
                  {t("equipment.modals.deactivate_equipment_description_suffix")}
                </>
              )
          }
        } else if (action === "delete") {
          switch (mode) {
            case EQUIPMENT_DELETE_DEACTIVATE_MODES.ASSIGNED_EXPERIENCES_NO_ACTIVE_BOOKING:
              subtitle = (
                <>
                  <strong>{equipmentName}</strong> {t("equipment.modals.is_assigned_to")} {assignedTripsCount} {t("equipment.modals.experiences")}:
                </>
              )
              break
            case EQUIPMENT_DELETE_DEACTIVATE_MODES.FULLY_SUBSTITUTABLE:
            case EQUIPMENT_DELETE_DEACTIVATE_MODES.PARTIALLY_SUBSTITUTABLE:
              subtitle = (
                <>
                  <strong>{equipmentName}</strong> {t("equipment.modals.is_assigned_to")} ({activeBookingsCount}) {t("equipment.modals.upcoming_trips")}. {t("equipment.modals.to_delete")} <strong>{equipmentName}</strong> {t("equipment.modals.you_must_replace_it")}.
                </>
              )
              break
            default:
              subtitle = (
                <>
                  {t("equipment.modals.delete_equipment_description_prefix")}
                  <strong>{equipmentName}</strong>
                  {t("equipment.modals.delete_equipment_description_suffix")}
                </>
              )
          }
        }

        const isNoExperiencesMode = mode === EQUIPMENT_DELETE_DEACTIVATE_MODES.NO_EXPERIENCES

        // Calculate loading states based on action type to avoid nested ternaries
        const isDeactivateAction = deactivateDeleteModalState.action === "deactivate"
        const actionLoading = isDeactivateAction ? deactivateLoading : deleteLoading
        const detailsLoadingState = isDeactivateAction ? deactivationDetailsLoading : deletionDetailsLoading
        const combinedLoading = actionLoading || detailsLoadingState

        return (
          <>
            {deactivateDeleteModalState?.visible && isNoExperiencesMode && (
              <NoExperiencesDeleteDeactivateModal
                open={deactivateDeleteModalState.visible}
                title={
                  deactivateDeleteModalState.action === "deactivate"
                    ? t("equipment.modals.deactivate_equipment_title")
                    : t("equipment.modals.delete_equipment_title")
                }
                description={
                  deactivateDeleteModalState.action === "deactivate"
                    ? t("equipment.modals.deactivate_equipment_description", { title: equipmentTitle })
                    : t("equipment.modals.delete_equipment_description", { title: equipmentTitle })
                }
                onCancel={closeDeactivateDeleteModal}
                onOk={handleDeactivateDeleteOk}
                okText={deactivateDeleteModalState.action === "deactivate" ? t("global.deactivate") : t("global.delete")}
                cancelText={t("global.cancel")}
                loading={actionLoading}
              />
            )}
            {deactivateDeleteModalState?.visible && !isNoExperiencesMode && mode && (
              <DeleteDeactivateModal
                visible={deactivateDeleteModalState.visible}
                onCancel={closeDeactivateDeleteModal}
                onOk={handleDeactivateDeleteOk}
                title={
                  deactivateDeleteModalState.action === "deactivate"
                    ? t("equipment.modals.deactivate_equipment_title")
                    : t("equipment.modals.delete_equipment_title")
                }
                subtitle={subtitle}
                okText={deactivateDeleteModalState.action === "deactivate" ? t("global.deactivate") : t("global.delete")}
                cancelText={t("global.cancel")}
                loading={combinedLoading}
                mode={mode}
                list={details.assigned_trips || []}
                upcomingBookings={details.active_bookings || []}
                assignedExperiences={details.assigned_trips || []}
                warning={getWarningMessage()}
                expMessage={expMessage}
                bookingMessage={bookingMessage}
              />
            )}
          </>
        )
      })()}

      {/* Video Player Modal */}
      <VideoPlayerModal
        open={videoPlayerOpen}
        selectedMedia={selectedMedia}
        handleClose={handleCloseVideoPlayer}
      />

      {/* Assign Experiences Modal */}
      <AssignExperiencesModal
        open={showAssignExperiencesModal}
        onCancel={handleAssignExperiencesModalClose}
        equipmentId={id}
        currentExperiences={equipment?.trips || []}
      />
    </div>
  )
}

export default EquipmentDetails
